import { Injectable } from '@angular/core';
import { ChannelsService } from '@app/shared/services/channels.service';
import { Channel } from '@shared/models/classes/channels/Channel';
import { OAuthService } from 'angular-oauth2-oidc';
import { BaseService } from 'projects/shared/src/lib/shared/base/services';
import { credentialsKey } from 'projects/shared/src/lib/shared/constants';
import { HttpCacheService } from 'projects/shared/src/lib/shared/core/http/http-cache.service';
import { __ } from 'projects/shared/src/lib/shared/functions/object.functions';
import { ApplicationUser } from 'projects/shared/src/lib/shared/models/classes/users/ApplicationUser';
import { IdentityToken } from 'projects/shared/src/lib/shared/models/classes/users/IdentityToken';
import { Role } from 'projects/shared/src/lib/shared/models/classes/users/Role';
import { firstValueFrom, Observable, of, Subject, zip } from 'rxjs';

import { HttpService } from '../http/http.service';
import { authCodeFlowConfig } from './oauth-config';
import { UsersService } from './../../shared/services/users.service';
import { environment } from '@env/environment';

/**
 * Provides a base for authentication workflow.
 * The Credentials interface as well as login/logout methods should be replaced with proper implementation.
 */
@Injectable()
export class AuthenticationService extends BaseService {

  // -----------------------------------------------------------------------------------------------------
  // @ READ ONLY VARIABLES
  // -----------------------------------------------------------------------------------------------------

  // -----------------------------------------------------------------------------------------------------
  // @ PRIVATE INSTANCE VARIABLES
  // -----------------------------------------------------------------------------------------------------

  private _credentials: IdentityToken | null;

  private _credentials$: Subject<IdentityToken> = new Subject<IdentityToken>();

  // tslint:disable-next-line:member-ordering
  credentials$: Observable<IdentityToken> = this._credentials$.asObservable();

  // -----------------------------------------------------------------------------------------------------
  // @ CONSTRUCTOR
  // -----------------------------------------------------------------------------------------------------

  constructor(
    private httpService: HttpService,
    private httpCacheService: HttpCacheService,
    public oAuthService: OAuthService,
    private channelsService: ChannelsService,
    private usersService: UsersService
  ) {
    super();

    const savedCredentials = localStorage.getItem(credentialsKey); // sessionStorage.getItem(credentialsKey) ||

    if (!__.IsNullOrUndefined(savedCredentials)) {
      this._credentials = JSON.parse(savedCredentials);
    }
  }

  // -----------------------------------------------------------------------------------------------------
  // @ PUBLIC METHODS
  // -----------------------------------------------------------------------------------------------------

  get user(): ApplicationUser {
    return this._credentials?.user;
  }

  get ticket(): any {
    return {
      accessToken: this._credentials.access_token,
      expires: this._credentials.expires,
      refreshToken: this._credentials.refresh_token
    };
  }

  hasRole(group: string) {
    if (__.IsNullOrUndefinedOrEmpty(group)) {
      return true;
    }
    if (environment.testPermissions === false && environment.production !== true) {
      return true;
    }
    if (!__.IsNullOrUndefined(this.credentials.user.roles)) {
      if (this.isCompanyAdministrator()) {
        return true;
      }
      return this.credentials.user.roles.some(q => q === group);
    }
    return false;
  }

  hasNotRole(group: string): boolean {
    if (__.IsNullOrUndefinedOrEmpty(group)) {
      return false;
    }
    if (environment.testPermissions === false && environment.production !== true) {
      return false;
    }
    if (!__.IsNullOrUndefined(this.credentials.user.roles)) {
      return !this.credentials.user.roles.some(q => q === group);
    }
    return true;
  }

  hasAnyRole(groups: string[]) {
    if (groups.length === 0) {
      return true;
    }
    if (environment.testPermissions === false && environment.production !== true) {
      return true;
    }
    if (!__.IsNullOrUndefined(this.credentials.user.roles)) {
      if (this.isCompanyAdministrator()) {
        return true;
      }

      return groups.findIndex(a => this.credentials.user.roles.some(q => q === a)) > -1;
    }
    return false;
  }

  isCompanyAdministrator(): boolean {
    return this.credentials.user.roles.indexOf(Role.CompanyAdministrator) > -1;
  }

  /**
   * If user email starts with julien, the user is super admin
   * @returns true if user email starts with julien otherwise return false
   */
  isSuperAdmin(): boolean {
    return this.user.email.startsWith('julien');
  }

  isAdministrator(): boolean {
    return this.credentials.user.roles.indexOf(Role.Administrator) > -1;
  }

  isAnyAdministrator(): boolean {
    return this.isAdministrator() || this.isCompanyAdministrator();
  }

  /**
   * Logs out the user and clear credentials.
   * @return True if the user was logged out successfully.
   */
  logout(): Observable<boolean> {
    this.oAuthService.logOut({ client_id: environment.loginClientId });
    this.setCredentials(null);

    return of(true);
  }

  /**
   * Checks is the user is authenticated.
   * @return True if the user is authenticated.
   */
  isAuthenticated(): boolean {
    return !__.IsNullOrUndefined(this._credentials) && !__.IsNullOrUndefined(this._credentials.user);
  }

  /**
   * Gets the user credentials.
   * @return The user credentials or null if the user is not authenticated.
   */
  get credentials(): IdentityToken | null {
    return this._credentials;
  }

  /**
   * Sets the user credentials.
   * The credentials may be persisted across sessions by setting the `remember` parameter to true.
   * Otherwise, the credentials are only persisted for the current session.
   * @param credentials The user credentials.
   * @param remember True to remember credentials across sessions.
   */
  setCredentials(credentials?: IdentityToken, remember?: boolean) {
    this._credentials = credentials || null;
    this._credentials$.next(this._credentials);

    if (credentials) {
      localStorage.setItem(credentialsKey, JSON.stringify(credentials));
    } else {
      localStorage.removeItem(credentialsKey);
    }
  }

  /**
   * Makes sure the user is logged in: a valid access token is used as is, an expired one is refreshed silently
   * with the refresh token, and only when that is not possible (no refresh token, refresh rejected) the login
   * redirect to the identity provider is started. A login callback (`?code=`) always completes the login.
   *
   * @returns {Promise<boolean>} Whether the application can be displayed.
   */
  checkLoginStatusOrLogin(): Promise<any> {
    this.oAuthService.configure(authCodeFlowConfig);

    if (this.oAuthService.hasValidAccessToken()) {
      if (__.IsNullOrUndefined(this.credentials?.user)) {
        this.logout().subscribe();
      }

      return this.oAuthService.loadDiscoveryDocument();
    }

    const isLoginCallback = window.location.search.includes('code=');
    const refreshToken = this.oAuthService.getRefreshToken() ?? this._credentials?.refresh_token;

    if (!isLoginCallback && !__.IsNullOrUndefinedOrEmpty(refreshToken)) {
      return this.oAuthService
        .loadDiscoveryDocument()
        .then(() => this.oAuthService.refreshToken())
        .then(() => this.completeLogin())
        .catch(() => this.loginWithRedirect());
    }

    return this.loginWithRedirect();
  }

  /**
   * Starts the login (redirect to the identity provider) or, on the login callback, exchanges the code.
   */
  private loginWithRedirect(): Promise<boolean> {
    return this.oAuthService
      .loadDiscoveryDocumentAndLogin({ customHashFragment: window.location.search })
      .then(() => this.completeLogin());
  }

  /**
   * Stores the tokens and loads the current user and channel.
   *
   * @returns {Promise<boolean>} Whether a valid access token is present.
   */
  private completeLogin(): Promise<boolean> {
    if (!this.oAuthService.hasValidAccessToken()) {
      return firstValueFrom(of(false));
    }

    const token = Object.assign(new IdentityToken(), {
      access_token: this.oAuthService.getAccessToken(),
      refresh_token: this.oAuthService.getRefreshToken(),
      expires: this.oAuthService.getAccessTokenExpiration().toString(),
    } as IdentityToken);

    this.setCredentials(token);

    const decodedToken = this.decodeToken(this.oAuthService.getIdToken());

    return firstValueFrom(
      zip(
        this.usersService.getCurrentUser(),
        this.channelsService.getById(decodedToken.channelId)
      )
    ).then((response: [ApplicationUser, Channel]) => {
      token.user = response[0];
      token.channel = response[1];

      this.setCredentials(token);

      return true;
    });
  }

  decodeToken(token: string) {
    var base64Url = token.split('.')[1];
    var base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    var jsonPayload = decodeURIComponent(window.atob(base64).split('').map(function (c) {
      return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));

    return JSON.parse(jsonPayload);
  }
}

export class AccessTokenRequestBody {
  email: string;
  password: string;
}

export class AccessTokenResponse {
  access_token: string;
  expires: string;
  refresh_token: string;
}
